import { useFindMany } from "@gadgetinc/react";
import { ArrowDownTrayIcon, PlusIcon } from "@heroicons/react/16/solid";
import { useState } from "react";
import { api } from "../../../api";
import { formatDateTime } from "../../../functions/date-utils";
import { getMappedFieldsSummary } from "../../../functions/whatsappFlowFields";
import { OverflowFrame } from "../../../externalComponents";
import LoadingSpinner from "../../../externalComponents/LoadingSpinner";
import { PageHeading } from "../../../externalComponents/PageHeading";
import { SettingsSidebar } from "../../../externalComponents/SettingsSidebar";
import { Badge } from "../../../externalComponents/tailwind/badge";
import { Button } from "../../../externalComponents/tailwind/button";
import EmptyState from "../../../externalComponents/tailwind/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../externalComponents/tailwind/table";
import AddWhatsappFlowModal from "./AddWhatsappFlowModal";
import ExportWhatsappFlowModal from "./ExportWhatsappFlowModal";
import WhatsappFlowMappingModal from "./WhatsappFlowMappingModal";

function NameListCell({ items, getName }) {
  if (!items || items.length === 0) {
    return <span className="text-gray-400">—</span>;
  }

  const name = getName(items[0]);
  const remaining = items.length - 1;

  return (
    <span className="flex items-center gap-2">
      <span className="max-w-[160px] truncate" title={name}>
        {name}
      </span>
      {remaining > 0 && <Badge color="zinc">+{remaining}</Badge>}
    </span>
  );
}

function getUniqueCampaigns(templates) {
  return Array.from(
    new Map(
      (templates || [])
        .map((template) => template.campaign)
        .filter(Boolean)
        .map((campaign) => [campaign.id, campaign]),
    ).values(),
  );
}

function MappedFieldsCell({ flow }) {
  const rows = flow.whatsappFlowScreens?.edges?.map((edge) => edge.node) || [];
  const { mapped, total } = getMappedFieldsSummary(rows);

  if (total === 0) {
    return <span className="text-gray-400">—</span>;
  }

  const color = mapped === total ? "green" : mapped > 0 ? "orange" : "zinc";

  return (
    <Badge color={color}>
      {mapped} / {total}
    </Badge>
  );
}

export default function WhatsappFlowsPage() {
  const [showAddFlowModal, setShowAddFlowModal] = useState(false);
  const [mappingFlowId, setMappingFlowId] = useState(null);
  const [exportFlow, setExportFlow] = useState(null);

  const [{ data: flows, fetching }] = useFindMany(api.whatsappFlow, {
    live: true,
    select: {
      id: true,
      name: true,
      flowId: true,
      lastExportedAt: true,
      whatsappTemplate: {
        edges: {
          node: {
            id: true,
            whatsappTemplateName: true,
            campaign: {
              id: true,
              name: true,
            },
          },
        },
      },
      whatsappFlowScreens: {
        edges: {
          node: {
            id: true,
            customField: true,
          },
        },
      },
    },
    sort: {
      createdAt: "Descending",
    },
  });

  const handleAddFlow = () => {
    setShowAddFlowModal(true);
  };

  const handleEditFlow = (flow) => {
    setMappingFlowId(flow.id);
  };

  const handleExportFlow = (flow) => {
    setExportFlow(flow);
  };

  return (
    <>
      <SettingsSidebar>
        <PageHeading
          title="WhatsApp flows"
          primaryAction="Add a flow"
          iconPrimaryAction={<PlusIcon />}
          handlePrimaryAction={handleAddFlow}
        />
        <OverflowFrame>
          <div className="mx-3 md:mx-0">
            <p className="my-4 text-sm text-gray-500">
              Configure WhatsApp flows to track responses and map their data
              fields to your custom fields.
            </p>

            {fetching ? (
              <LoadingSpinner />
            ) : flows && flows.length > 0 ? (
              <Table className="mb-4">
                <TableHead>
                  <TableRow>
                    <TableHeader>Flow name</TableHeader>
                    <TableHeader>Template</TableHeader>
                    <TableHeader>Campaign</TableHeader>
                    <TableHeader className="text-center">
                      Mapped fields
                    </TableHeader>
                    <TableHeader className="text-center">
                      Last export
                    </TableHeader>
                    <TableHeader className="text-center"></TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {flows.map((flow) => (
                    <TableRow
                      key={flow.id}
                      onClick={() => handleEditFlow(flow)}
                    >
                      <TableCell className="font-semibold">
                        {flow.name || `Flow ${flow.flowId ?? flow.id}`}
                      </TableCell>
                      <TableCell>
                        <NameListCell
                          items={flow.whatsappTemplate?.edges?.map(
                            (edge) => edge.node,
                          )}
                          getName={(template) => template.whatsappTemplateName}
                        />
                      </TableCell>
                      <TableCell>
                        <NameListCell
                          items={getUniqueCampaigns(
                            flow.whatsappTemplate?.edges?.map(
                              (edge) => edge.node,
                            ),
                          )}
                          getName={(campaign) => campaign.name}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="text-center">
                          <MappedFieldsCell flow={flow} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-center">
                          {flow.lastExportedAt
                            ? formatDateTime(flow.lastExportedAt, "mediumDate")
                            : "—"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end">
                          <Button
                            outline
                            className="w-full min-w-[140px] justify-center"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExportFlow(flow);
                            }}
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            Export
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <EmptyState
                heading="You have no WhatsApp flows yet"
                content="Add a WhatsApp flow to start tracking responses and mapping their data fields to your custom fields."
                action={handleAddFlow}
                icon={<PlusIcon />}
                actionText="Add a flow"
              />
            )}
          </div>
        </OverflowFrame>
      </SettingsSidebar>

      <AddWhatsappFlowModal
        open={showAddFlowModal}
        onClose={() => setShowAddFlowModal(false)}
        onCreated={(newFlowId) => {
          setShowAddFlowModal(false);
          setMappingFlowId(newFlowId);
        }}
      />

      {mappingFlowId && (
        <WhatsappFlowMappingModal
          flowId={mappingFlowId}
          open={!!mappingFlowId}
          onClose={() => setMappingFlowId(null)}
          onSaved={() => setMappingFlowId(null)}
        />
      )}

      <ExportWhatsappFlowModal
        flow={exportFlow}
        open={!!exportFlow}
        onClose={() => setExportFlow(null)}
      />
    </>
  );
}
